/**
 * Гарантийные обращения заказчика (ДГ-22) и оценки от 1 до 5 (ДГ-27, КА-15) в карточке договора.
 * Договор с заказчиком: обращения со сроками реакции и устранения, фото и акты, расходы, встречная претензия
 * и оценка заказчика. Расходный договор: оценки поставщика или подрядчика (после поступления на склад — по
 * каждой поставке). Оценка 3 и ниже уходит руководителю проекта сразу — об этом сказано в форме.
 */
import {
  type ContractWarranty,
  type FileInfo,
  RATING_KINDS,
  type RatingKind,
  type RatingRow,
  WARRANTY_STATUSES,
  type WarrantyClaimRow,
  type WarrantyStatus,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Paperclip, Plus, Star } from 'lucide-react';
import { useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { date, tenge, todayLocal } from '../../lib/format';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, ErrorBox, toast } from '../../ui/feedback';
import { Field, Input, Select, Textarea } from '../../ui/form';
import { Panel } from '../../ui/layout';

const hidden = (err: unknown) => err instanceof ApiError && (err.status === 403 || err.status === 404);

const STATUS_TONE: Record<WarrantyStatus, 'info' | 'warning' | 'success' | 'neutral'> = {
  new: 'warning',
  in_work: 'info',
  resolved: 'success',
  rejected: 'neutral',
};

/** Пять кнопок-звёзд: каждая не меньше 44 px, выбранная подписана числом */
function ScoreInput({ value, onChange }: { value: number; onChange: (n: number) => void }) {
  return (
    <fieldset aria-label="Оценка от 1 до 5" className="m-0 flex min-w-0 flex-wrap gap-1 border-0 p-0">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          aria-pressed={value === n}
          aria-label={`${n} из 5`}
          onClick={() => onChange(n)}
          className={`inline-flex size-11 items-center justify-center gap-0.5 rounded-md border text-[14px] font-medium ${
            value === n ? 'border-accent bg-accent-soft text-ink' : 'border-line text-ink-2 hover:bg-tile'
          }`}
        >
          <Star className={`size-4 ${n <= value ? 'fill-current' : ''}`} aria-hidden />
          {n}
        </button>
      ))}
    </fieldset>
  );
}

function RatingDialog({
  contractId,
  kinds,
  threshold,
  source,
  onClose,
  onSaved,
}: {
  contractId: string;
  kinds: RatingKind[];
  threshold: number;
  source: { type: 'manual' | 'claim' | 'receipt'; id: string | null; label: string | null };
  onClose: () => void;
  onSaved: () => void;
}) {
  const [f, setF] = useState({ kind: kinds[0] ?? 'supplier', score: 0, comment: '', raterName: '' });
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const low = f.score > 0 && f.score <= threshold;
  const save = useMutation({
    mutationFn: () =>
      api<{ id: string; escalated: boolean }>(`/contracts/${contractId}/ratings`, {
        method: 'POST',
        body: {
          kind: f.kind,
          score: f.score,
          comment: f.comment.trim() || undefined,
          raterName: f.kind === 'customer' ? f.raterName.trim() || undefined : undefined,
          sourceType: source.type,
          sourceId: source.id ?? undefined,
        },
      }),
    onSuccess: (r) => {
      onSaved();
      onClose();
      toast(r.escalated ? 'Оценка сохранена, руководитель проекта уведомлён' : 'Оценка сохранена');
    },
  });
  const ready = f.score > 0 && (!low || f.comment.trim().length > 0);
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title="Оценка"
      description={source.label ?? 'Оценка от 1 до 5 с комментарием: входит в рейтинг контрагента.'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="primary" loading={save.isPending} disabled={!ready} onClick={() => save.mutate()}>
            Оценить
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {kinds.length > 1 ? (
          <Field label="Кого оцениваете">
            <Select value={f.kind} onChange={(e) => set('kind', e.target.value as RatingKind)}>
              {kinds.map((k) => (
                <option key={k} value={k}>
                  {RATING_KINDS[k]}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
        <Field label="Оценка">
          <ScoreInput value={f.score} onChange={(n) => set('score', n)} />
        </Field>
        {f.kind === 'customer' ? (
          <Field label="Кто оценил со стороны заказчика">
            <Input value={f.raterName} maxLength={200} onChange={(e) => set('raterName', e.target.value)} />
          </Field>
        ) : null}
        <Field
          label={low ? 'Что не так (обязательно)' : 'Комментарий'}
          hint={low ? `Оценка ${threshold} и ниже сразу уйдёт руководителю проекта.` : undefined}
        >
          <Textarea
            value={f.comment}
            maxLength={2000}
            rows={3}
            onChange={(e) => set('comment', e.target.value)}
          />
        </Field>
      </div>
    </Dialog>
  );
}

function ClaimDialog({
  contractId,
  d,
  onClose,
  onSaved,
}: {
  contractId: string;
  d: ContractWarranty;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [f, setF] = useState({
    title: '',
    description: '',
    reporterName: '',
    reportedOn: todayLocal(),
    fixDueOn: '',
    assigneeId: '',
  });
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const save = useMutation({
    mutationFn: () =>
      api(`/contracts/${contractId}/warranty/claims`, {
        method: 'POST',
        body: {
          title: f.title.trim(),
          description: f.description.trim() || undefined,
          reporterName: f.reporterName.trim() || undefined,
          reportedOn: f.reportedOn,
          fixDueOn: f.fixDueOn || undefined,
          assigneeId: f.assigneeId || undefined,
        },
      }),
    onSuccess: () => {
      onSaved();
      onClose();
      toast('Обращение добавлено');
    },
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title="Гарантийное обращение"
      description={`Ответить заказчику нужно за ${d.settings.reactionDays} дн., устранить — за ${d.settings.fixDays} дн., если не согласован другой срок.`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={f.title.trim().length < 3}
            onClick={() => save.mutate()}
          >
            Добавить
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Что случилось">
          <Input value={f.title} maxLength={300} onChange={(e) => set('title', e.target.value)} />
        </Field>
        <Field label="Подробности">
          <Textarea
            value={f.description}
            rows={3}
            maxLength={4000}
            onChange={(e) => set('description', e.target.value)}
          />
        </Field>
        <Field label="Кто обратился">
          <Input
            value={f.reporterName}
            maxLength={200}
            onChange={(e) => set('reporterName', e.target.value)}
          />
        </Field>
        <Field label="Дата обращения">
          <Input
            type="date"
            value={f.reportedOn}
            max={todayLocal()}
            onChange={(e) => set('reportedOn', e.target.value)}
          />
        </Field>
        <Field label="Срок устранения, согласованный с заказчиком" hint="Если пусто — по настройке компании.">
          <Input
            type="date"
            value={f.fixDueOn}
            min={f.reportedOn}
            onChange={(e) => set('fixDueOn', e.target.value)}
          />
        </Field>
        <Field label="Ответственный">
          <Select value={f.assigneeId} onChange={(e) => set('assigneeId', e.target.value)}>
            <option value="">Пока не назначен</option>
            {d.people.map((p) => (
              <option key={p.id} value={p.id}>
                {p.fullName}
              </option>
            ))}
          </Select>
        </Field>
      </div>
    </Dialog>
  );
}

function ClaimFiles({ claimId, canEdit }: { claimId: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const key = ['files', 'warranty_claim', claimId];
  const files = useQuery({
    queryKey: key,
    queryFn: () => api<FileInfo[]>('/files', { query: { entityType: 'warranty_claim', entityId: claimId } }),
    retry: false,
  });
  const upload = useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('entityType', 'warranty_claim');
      form.append('entityId', claimId);
      form.append('file', file);
      return api<FileInfo>('/files', { form, method: 'POST' });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key });
      toast('Файл приложен');
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px]">
      {files.data?.map((f) => (
        <a
          key={f.id}
          href={`/api/v1/files/${f.id}/download`}
          className="text-accent-text [overflow-wrap:anywhere] hover:underline"
        >
          {f.name}
        </a>
      ))}
      {canEdit ? (
        <label className="inline-flex min-h-11 cursor-pointer items-center gap-1.5 rounded-md border border-line px-3 text-[13px] font-medium text-ink hover:bg-tile md:min-h-9">
          <input
            type="file"
            className="sr-only"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) upload.mutate(f);
              e.target.value = '';
            }}
          />
          <Paperclip className="size-4" aria-hidden />
          {upload.isPending ? 'Загрузка…' : 'Фото или акт'}
        </label>
      ) : null}
    </div>
  );
}

function ResolveDialog({
  claim,
  moneyHidden,
  onClose,
  onSaved,
}: {
  claim: WarrantyClaimRow;
  moneyHidden: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [resolution, setResolution] = useState('');
  const [cost, setCost] = useState('');
  const save = useMutation({
    mutationFn: () =>
      api(`/contracts/warranty/claims/${claim.id}/resolve`, {
        method: 'POST',
        body: { resolution: resolution.trim(), costAmount: cost ? Number(cost) : undefined },
      }),
    onSuccess: () => {
      onSaved();
      onClose();
      toast('Обращение закрыто');
    },
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title="Устранено"
      description={claim.title}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={resolution.trim().length < 3}
            onClick={() => save.mutate()}
          >
            Закрыть обращение
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Что сделано">
          <Textarea
            value={resolution}
            rows={3}
            maxLength={4000}
            onChange={(e) => setResolution(e.target.value)}
          />
        </Field>
        {moneyHidden ? null : (
          <Field label="Расходы на устранение, ₸">
            <Input type="number" min={0} value={cost} onChange={(e) => setCost(e.target.value)} />
          </Field>
        )}
      </div>
    </Dialog>
  );
}

function ClaimLine({
  c,
  d,
  contractId,
  onChanged,
}: {
  c: WarrantyClaimRow;
  d: ContractWarranty;
  contractId: string;
  onChanged: () => void;
}) {
  const [dlg, setDlg] = useState<'resolve' | 'rate' | null>(null);
  const [counter, setCounter] = useState(false);
  const [cid, setCid] = useState('');
  const act = useMutation({
    mutationFn: (p: { path: string; body: unknown }) =>
      api(`/contracts/warranty/claims/${c.id}/${p.path}`, { method: 'POST', body: p.body }),
    onSuccess: () => {
      onChanged();
      setCounter(false);
      toast('Сохранено');
    },
  });
  const open = c.status === 'new' || c.status === 'in_work';
  const reject = () => {
    const reason = window.prompt('Почему это не гарантийный случай? (не короче 3 символов)');
    if (reason && reason.trim().length >= 3) act.mutate({ path: 'reject', body: { reason: reason.trim() } });
  };
  return (
    <li className="flex flex-col gap-2 py-3 text-[14px]">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-ink">{c.number}</span>
        <span className="min-w-0 text-ink [overflow-wrap:anywhere]">{c.title}</span>
        <Badge tone={STATUS_TONE[c.status as WarrantyStatus] ?? 'neutral'}>
          {WARRANTY_STATUSES[c.status as WarrantyStatus] ?? c.status}
        </Badge>
        {c.reactionLate ? <Badge tone="danger">реакция позже срока</Badge> : null}
        {c.fixLate ? <Badge tone="danger">устранение позже срока</Badge> : null}
        {c.outOfWarranty ? <Badge tone="warning">после гарантийного срока</Badge> : null}
      </div>
      <div className="text-[12px] text-ink-2">
        {c.reporterName ? `${c.reporterName} · ` : ''}обращение {date(c.reportedOn)} · ответить до{' '}
        {date(c.reactionDueOn)} · устранить до {date(c.fixDueOn)}
        {open && c.fixDaysLeft != null
          ? c.fixDaysLeft < 0
            ? ` (просрочено на ${Math.abs(c.fixDaysLeft)} дн.)`
            : ` (осталось ${c.fixDaysLeft} дн.)`
          : ''}
        {c.assigneeName ? ` · отвечает ${c.assigneeName}` : ''}
      </div>
      {c.description ? <p className="text-[13px] text-ink-2">{c.description}</p> : null}
      {c.resolution ? (
        <p className="text-[13px] text-ink-2">
          {c.status === 'rejected' ? 'Причина отказа' : 'Сделано'}: {c.resolution}
          {c.costAmount != null ? ` · расходы ${tenge(c.costAmount)}` : ''}
        </p>
      ) : null}
      {c.counterContractNumber ? (
        <p className="text-[13px] text-ink-2">
          Встречная претензия: договор {c.counterContractNumber} · {c.counterCounterpartyName}
          {c.counterWarrantyUntil
            ? ` · гарантия до ${date(c.counterWarrantyUntil)}`
            : ' · гарантия не заведена'}
          {c.counterNote ? ` · ${c.counterNote}` : ''}
        </p>
      ) : null}
      <ClaimFiles claimId={c.id} canEdit={d.canEdit} />
      {d.canEdit ? (
        <div className="flex flex-wrap items-center gap-2">
          {c.status === 'new' ? (
            <Button
              size="sm"
              variant="secondary"
              loading={act.isPending}
              onClick={() => act.mutate({ path: 'take', body: {} })}
            >
              Взять в работу
            </Button>
          ) : null}
          {open ? (
            <>
              <Button size="sm" variant="secondary" onClick={() => setDlg('resolve')}>
                Устранено
              </Button>
              <Button size="sm" variant="ghost" loading={act.isPending} onClick={reject}>
                Не гарантийный случай
              </Button>
            </>
          ) : null}
          {d.counterContracts.length ? (
            <Button size="sm" variant="ghost" onClick={() => setCounter((v) => !v)}>
              Встречная претензия
            </Button>
          ) : null}
          {!open && !c.rated && c.status === 'resolved' ? (
            <Button size="sm" variant="secondary" onClick={() => setDlg('rate')}>
              Оценка заказчика
            </Button>
          ) : null}
        </div>
      ) : null}
      {counter ? (
        <div className="flex flex-wrap items-end gap-2">
          <Field label="Договор поставщика или подрядчика">
            <Select value={cid} onChange={(e) => setCid(e.target.value)}>
              <option value="">Выберите</option>
              {d.counterContracts.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.label}
                </option>
              ))}
            </Select>
          </Field>
          <Button
            size="sm"
            variant="primary"
            disabled={!cid}
            loading={act.isPending}
            onClick={() => act.mutate({ path: 'counter', body: { counterContractId: cid } })}
          >
            Привязать
          </Button>
        </div>
      ) : null}
      {dlg === 'resolve' ? (
        <ResolveDialog
          claim={c}
          moneyHidden={d.moneyHidden}
          onClose={() => setDlg(null)}
          onSaved={onChanged}
        />
      ) : null}
      {dlg === 'rate' ? (
        <RatingDialog
          contractId={contractId}
          kinds={['customer']}
          threshold={d.settings.alertScore}
          source={{ type: 'claim', id: c.id, label: `Обращение ${c.number}: ${c.title}` }}
          onClose={() => setDlg(null)}
          onSaved={onChanged}
        />
      ) : null}
    </li>
  );
}

function RatingLine({ r }: { r: RatingRow }) {
  return (
    <li className="flex flex-col gap-1 py-3 text-[14px]">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={r.low ? 'danger' : r.score === 5 ? 'success' : 'info'}>{r.score} из 5</Badge>
        <span className="text-ink">{RATING_KINDS[r.kind as RatingKind] ?? r.kind}</span>
        <span className="text-[12px] text-ink-2">
          {date(r.ratedOn)}
          {r.raterName ? ` · ${r.raterName}` : r.createdByName ? ` · ${r.createdByName}` : ''}
          {r.sourceLabel ? ` · ${r.sourceLabel}` : ''}
        </span>
      </div>
      {r.comment ? <p className="text-[13px] text-ink-2">{r.comment}</p> : null}
    </li>
  );
}

export function ContractWarrantyPanel({
  contractId,
  direction,
  terminated,
}: {
  contractId: string;
  direction: 'revenue' | 'expense';
  terminated?: boolean;
}) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['ctr', 'warranty', contractId],
    queryFn: () => api<ContractWarranty>(`/contracts/${contractId}/warranty`),
    retry: false,
  });
  const [dlg, setDlg] = useState<'claim' | { receipt: { id: string; label: string } | null } | null>(null);
  if (q.isPending || q.isError) return q.isError && !hidden(q.error) ? <ErrorBox error={q.error} /> : null;
  const d = q.data;
  const changed = () => {
    void qc.invalidateQueries({ queryKey: ['ctr', 'warranty'] });
    void qc.invalidateQueries({ queryKey: ['inbox'] });
  };
  const rating = d.counterpartyRating;
  const canRate = d.ratingKinds.length > 0;
  return (
    <>
      {direction === 'revenue' ? (
        <Panel
          title="Гарантийные обращения"
          meta={
            d.warrantyUntil
              ? `гарантийный срок по объекту до ${date(d.warrantyUntil)}`
              : 'обращения заказчика после сдачи объекта'
          }
          actions={
            d.canEdit && !terminated ? (
              <Button
                size="sm"
                variant="secondary"
                icon={<Plus className="size-4" />}
                onClick={() => setDlg('claim')}
              >
                Добавить
              </Button>
            ) : null
          }
        >
          {d.claims.length ? (
            <ul className="divide-y divide-line">
              {d.claims.map((c) => (
                <ClaimLine key={c.id} c={c} d={d} contractId={contractId} onChanged={changed} />
              ))}
            </ul>
          ) : (
            <p className="text-[14px] text-ink-2">
              Обращений нет. Когда заказчик сообщит о дефекте после сдачи, добавьте обращение: портал напомнит
              о сроках ответа и устранения.
            </p>
          )}
          {d.moneyHidden && d.claims.length ? (
            <p className="mt-2 text-[12px] text-ink-2">
              Расходы видны сотрудникам с доступом к суммам договоров.
            </p>
          ) : null}
        </Panel>
      ) : null}
      <Panel
        title="Оценки"
        meta={
          rating.count
            ? `средняя ${String(rating.average).replace('.', ',')} из 5 по ${rating.count} оценкам контрагента, низких — ${rating.low}`
            : 'оценок пока нет'
        }
        actions={
          canRate ? (
            <Button
              size="sm"
              variant="secondary"
              icon={<Plus className="size-4" />}
              onClick={() => setDlg({ receipt: null })}
            >
              Оценить
            </Button>
          ) : null
        }
      >
        {d.receiptsToRate.length ? (
          <div className="mb-3 flex flex-wrap items-center gap-2 text-[13px] text-ink-2">
            <span>Оцените поставки:</span>
            {d.receiptsToRate.map((r) => (
              <Button key={r.id} size="sm" variant="ghost" onClick={() => setDlg({ receipt: r })}>
                {r.label} от {date(r.docDate)}
              </Button>
            ))}
          </div>
        ) : null}
        {d.ratings.length ? (
          <ul className="divide-y divide-line">
            {d.ratings.map((r) => (
              <RatingLine key={r.id} r={r} />
            ))}
          </ul>
        ) : (
          <p className="text-[14px] text-ink-2">
            {direction === 'revenue'
              ? 'Оценка заказчика ставится после того, как обращение закрыто.'
              : 'Оценка от 1 до 5 с комментарием входит в рейтинг. Оценка 3 и ниже сразу уходит руководителю проекта.'}
          </p>
        )}
      </Panel>
      {dlg === 'claim' ? (
        <ClaimDialog contractId={contractId} d={d} onClose={() => setDlg(null)} onSaved={changed} />
      ) : null}
      {dlg && typeof dlg === 'object' ? (
        <RatingDialog
          contractId={contractId}
          kinds={d.ratingKinds}
          threshold={d.settings.alertScore}
          source={
            dlg.receipt
              ? { type: 'receipt', id: dlg.receipt.id, label: `Поступление ${dlg.receipt.label}` }
              : { type: 'manual', id: null, label: null }
          }
          onClose={() => setDlg(null)}
          onSaved={changed}
        />
      ) : null}
    </>
  );
}
